import { DirectoryCardErrorBoundary } from './DirectoryCardErrorBoundary';
import { FeaturedExpertCard } from './featuredExpertCard';

import { Card } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Skeleton } from '@/components/ui/skeleton';
import { VStack } from '@/components/ui/vstack';
import type { ActiveFeaturedExpertItem } from '@/services/getFeaturedExpertsV2';

type FeaturedExpertsSectionProps = {
  featuredExperts: ActiveFeaturedExpertItem[];
  isLoading?: boolean;
};

/**
 * Loading skeleton for featured expert card.
 */
function FullCardSkeleton() {
  return (
    <Card variant="outline" className="p-4">
      <HStack className="items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />
        <VStack className="flex-1 gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </VStack>
      </HStack>
      <VStack className="mt-3 gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </VStack>
    </Card>
  );
}

/**
 * Section component displaying featured experts in the desktop sidebar.
 *
 * Spacing design:
 * - gap-3 (12px): Standard spacing between cards for visual separation
 * - gap-4 (16px): Slightly larger spacing for heading to content
 *
 * Loading state: Shows skeleton cards while data is being fetched to prevent layout shift.
 * Empty state: Returns null when no featured experts to avoid showing empty section.
 */
export function FeaturedExpertsSection({
  featuredExperts,
  isLoading = false,
}: FeaturedExpertsSectionProps) {
  if (!isLoading && !featuredExperts.length) {
    return null;
  }

  if (isLoading) {
    return (
      <VStack className="gap-4">
        <Heading size="lg">Featured Experts</Heading>
        <VStack className="gap-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <FullCardSkeleton key={`skeleton-full-${index}`} />
          ))}
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack className="gap-4" testID="featured-experts-section">
      <Heading size="lg">Featured Experts</Heading>

      <VStack className="gap-3">
        {featuredExperts.map((item) => (
          <DirectoryCardErrorBoundary
            key={`${item.expert.id}-${item.cancerType.id}`}
            expertId={item.expert.id}>
            <FeaturedExpertCard item={item} />
          </DirectoryCardErrorBoundary>
        ))}
      </VStack>
    </VStack>
  );
}
